import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ListChecks, Plus, Loader2, Save, X, Calendar as CalIcon, Trash2,
  CircleDot, Circle, CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_META = {
  open:        { label: "Offen",      cls: "bg-amber-50 text-amber-700 border-amber-200",    Icon: Circle },
  in_progress: { label: "In Arbeit",  cls: "bg-cyan-50 text-cyan-700 border-cyan-200",       Icon: CircleDot },
  done:        { label: "Erledigt",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
};
const PRIO_META = {
  low:    { label: "Niedrig", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  normal: { label: "Normal",  cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  high:   { label: "Hoch",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const empty = { title: "", description: "", priority: "normal", due_date: "", status: "open" };

export default function Aufgaben() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quickTitle, setQuickTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = items.filter((t) => filter === "all" ? true : t.status === filter);
  const counts = items.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});
  const totalOpen = (counts.open || 0) + (counts.in_progress || 0);

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    try {
      await api.post("/tasks", { title: quickTitle, status: "open", priority: "normal" });
      setQuickTitle("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleDone = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    setItems((prev) => prev.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const cycleStatus = async (t) => {
    const order = ["open", "in_progress", "done"];
    const next = order[(order.indexOf(t.status) + 1) % order.length];
    setItems((prev) => prev.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try { await api.patch(`/tasks/${t.id}`, { status: next }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Aufgabe „${t.title}" löschen?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, due_date: t.due_date || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Titel ist Pflichtfeld");
    setSaving(true);
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editing) {
        await api.patch(`/tasks/${editing.id}`, payload);
        toast.success("Aufgabe aktualisiert");
      } else {
        await api.post("/tasks", payload);
        toast.success("Aufgabe angelegt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const isOverdue = (t) => t.due_date && t.status !== "done" && new Date(t.due_date) < new Date(new Date().toDateString());

  return (
    <Layout
      title="Aufgaben"
      subtitle={`${totalOpen} offen · ${counts.done || 0} erledigt`}
      action={
        <Button onClick={openCreate} data-testid="task-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Neue Aufgabe
        </Button>
      }
      primaryAction={{ label: "Neue Aufgabe", icon: Plus, onClick: openCreate, testid: "mobile-fab-new-task" }}
    >
      <div className="max-w-4xl space-y-5">
        {/* Quick add */}
        <Card className="p-4">
          <form onSubmit={quickAdd} className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-zinc-400 shrink-0" />
            <Input
              placeholder="Schnell-Aufgabe hinzufügen — Enter drücken"
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              className="border-0 focus-visible:ring-0 shadow-none px-0"
              data-testid="task-quick-add"
            />
            {quickTitle && (
              <Button size="sm" type="submit"><Save className="h-4 w-4" /></Button>
            )}
          </form>
        </Card>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="task-filters">
          {[
            { key: "all", label: `Alle · ${items.length}` },
            { key: "open", label: `Offen · ${counts.open || 0}` },
            { key: "in_progress", label: `In Arbeit · ${counts.in_progress || 0}` },
            { key: "done", label: `Erledigt · ${counts.done || 0}` },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`task-filter-${f.key}`}
              className={cn(
                "text-[12px] font-semibold px-3 py-1.5 rounded-full border transition",
                filter === f.key
                  ? "bg-emerald-600 text-white border-emerald-600 shadow"
                  : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : visible.length === 0 ? (
          <Card className="p-12 text-center" data-testid="task-empty">
            <div className="h-14 w-14 rounded-2xl brand-gradient-bg flex items-center justify-center mx-auto mb-4">
              <ListChecks className="h-6 w-6 text-white" />
            </div>
            <div className="font-display text-lg font-bold">Keine Aufgaben</div>
            <p className="text-sm text-zinc-500 mt-1">Lege eine erste Aufgabe an oder ändere den Filter.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden" data-testid="task-list">
            <ul className="divide-y divide-zinc-100">
              {visible.map((t) => {
                const SM = STATUS_META[t.status] || STATUS_META.open;
                const PM = PRIO_META[t.priority] || PRIO_META.normal;
                const overdue = isOverdue(t);
                return (
                  <li key={t.id} className="px-5 py-4 flex items-start gap-4 group hover:bg-zinc-50/40 transition" data-testid={`task-item-${t.id}`}>
                    <Checkbox
                      checked={t.status === "done"}
                      onCheckedChange={() => toggleDone(t)}
                      className="mt-1"
                      data-testid={`task-check-${t.id}`}
                    />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(t)}>
                      <div className={cn("font-display font-semibold tracking-tight", t.status === "done" && "line-through text-zinc-400")}>
                        {t.title}
                      </div>
                      {t.description && <p className={cn("text-sm text-zinc-500 mt-0.5 line-clamp-2", t.status === "done" && "line-through")}>{t.description}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); cycleStatus(t); }}
                          className={cn("inline-flex items-center gap-1 text-[10px] font-bold border rounded-full px-2 py-0.5", SM.cls)}
                        >
                          <SM.Icon className="h-3 w-3" />
                          {SM.label}
                        </button>
                        <span className={cn("text-[10px] font-bold border rounded-full px-2 py-0.5", PM.cls)}>{PM.label}</span>
                        {t.due_date && (
                          <span className={cn(
                            "inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2 py-0.5",
                            overdue ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"
                          )}>
                            {overdue ? <AlertTriangle className="h-3 w-3" /> : <CalIcon className="h-3 w-3" />}
                            {new Date(t.due_date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                          </span>
                        )}
                        {t.completed_at && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600">
                            <Clock className="h-3 w-3" />
                            erledigt {new Date(t.completed_at).toLocaleDateString("de-DE")}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      className="opacity-0 group-hover:opacity-100 transition text-rose-500 hover:bg-rose-50 rounded-md p-1.5 mt-0.5"
                      data-testid={`task-delete-${t.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Aufgabe bearbeiten" : "Neue Aufgabe"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Titel *</Label>
              <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-form-title" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Beschreibung</Label>
              <Textarea className="mt-1.5" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-desc" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Offen</SelectItem>
                    <SelectItem value="in_progress">In Arbeit</SelectItem>
                    <SelectItem value="done">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Priorität</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-form-prio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Niedrig</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Hoch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Fällig am</Label>
                <Input type="date" className="mt-1.5" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-form-due" />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-2" /> Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="task-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editing ? "Speichern" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
